import { createContext, useContext, useState } from "react";
import { Modal } from "antd";

const AlertContext = createContext(null);

export const GlobalAuthModal = ({ children }) => {
    const [alert, setAlert] = useState({
        open: false,
        title: "",
        description: "",
        buttons: []
    });

    const showAlert = (config) => {
        setAlert({
            open: true,
            title: config.title || "Alert",
            description: config.description || "",
            buttons: config.buttons || []
        });
    };

    const hideAlert = () => {
        setAlert((prev) => ({ ...prev, open: false }));
    };

    return (
        <AlertContext.Provider value={{ alert, showAlert, hideAlert }}>
            {children}
            <Modal
                title={null}
                open={alert.open}
                onCancel={hideAlert}
                footer={null}
                closable={true}
                centered
                width={460}
                zIndex={10000}
                styles={{
                    mask: {
                        backdropFilter: 'blur(8px)',
                        backgroundColor: 'rgba(15, 23, 42, 0.35)',
                    },
                    content: {
                        borderRadius: '24px',
                        padding: '36px 32px',
                        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.8)',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    }
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '20px' }}>
                    {/* Beautiful Premium Warning/Exit Icon */}
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #fed7aa',
                        boxShadow: '0 4px 12px rgba(251, 146, 60, 0.15)',
                    }}>
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                    </div>

                    {/* Title & Description */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <h2 style={{
                            fontSize: '22px',
                            fontWeight: 800,
                            color: '#0f172a',
                            margin: 0,
                            letterSpacing: '-0.5px',
                            lineHeight: 1.3
                        }}>
                            {alert.title}
                        </h2>
                        <p style={{
                            fontSize: '14px',
                            color: '#475569',
                            lineHeight: '1.6',
                            margin: 0,
                            maxWidth: '350px',
                            fontWeight: 500
                        }}>
                            {alert.description}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        width: '100%',
                        marginTop: '10px',
                        justifyContent: 'center'
                    }}>
                        {alert.buttons.length > 0 ? (
                            alert.buttons.map((btn, index) => {
                                const isPrimary = btn.variant === "primary";
                                return (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            if (btn.onClick) btn.onClick();
                                            hideAlert();
                                        }}
                                        style={{
                                            flex: 1,
                                            maxWidth: '170px',
                                            height: '46px',
                                            borderRadius: '14px',
                                            border: isPrimary ? 'none' : '1.5px solid #e2e8f0',
                                            background: isPrimary 
                                                ? 'linear-gradient(135deg, #ff8c00 0%, #ff4500 100%)' 
                                                : '#ffffff',
                                            color: isPrimary ? '#ffffff' : '#475569',
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            boxShadow: isPrimary 
                                                ? '0 6px 16px rgba(255, 69, 0, 0.25)' 
                                                : '0 2px 4px rgba(0,0,0,0.02)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            if (isPrimary) {
                                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 69, 0, 0.35)';
                                                e.currentTarget.style.filter = 'brightness(1.05)';
                                            } else {
                                                e.currentTarget.style.background = '#f8fafc';
                                                e.currentTarget.style.borderColor = '#cbd5e1';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'none';
                                            if (isPrimary) {
                                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 69, 0, 0.25)';
                                                e.currentTarget.style.filter = 'none';
                                            } else {
                                                e.currentTarget.style.background = '#ffffff';
                                                e.currentTarget.style.borderColor = '#e2e8f0';
                                            }
                                        }}
                                    >
                                        {btn.text}
                                    </button>
                                );
                            })
                        ) : (
                            <button
                                onClick={hideAlert}
                                style={{
                                    width: '120px',
                                    height: '42px',
                                    borderRadius: '14px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #ff8c00 0%, #ff4500 100%)',
                                    color: '#ffffff',
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    boxShadow: '0 6px 16px rgba(255, 69, 0, 0.25)',
                                }}
                            >
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </Modal>
        </AlertContext.Provider>
    );
};

export const useGlobalAuthModal = () => useContext(AlertContext);
